import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import {
  DialogComponent,
  DialogDataComponent,
  PopoverService,
  ToastFacade,
  UiPromiseService,
} from 'ngx-zcs-components';
import { MatButtonModule } from '@angular/material/button';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { AuthenticationService, InstallerIdentity, RoleService } from 'ngx-core-services';
import { map, shareReplay } from 'rxjs/operators';
import { Observable } from 'rxjs';

import { labels } from './view-certified.utils';

interface InstallerIdentityHelper {
  codInstallatore: string;
  emailCert: string;
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonicModule,
    MatButtonModule,
    MatDialogModule,
    MatListModule,
    MatIconModule,
    MatDividerModule,
  ],
  selector: 'lib-view-certified-operators',
  templateUrl: './view-certified-operators.component.html',
  styleUrls: ['./view-certified-operators.component.scss'],
})
export class ViewCertifiedOperatorsComponent {
  @Input() mode: 'mobile' | 'browser' = 'mobile';

  readonly _labels = labels;
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
  }

  get labels(): typeof labels {
    return this._labels;
  }

  @Output() certifiedOperatorChangeEvent = new EventEmitter<void>();

  readonly installers$: Observable<Partial<InstallerIdentityHelper>[]> = this.roleService.identities$.pipe(
    map((installer) => {
      const cInstaller: InstallerIdentity[] = (installer as InstallerIdentity[])?.filter((i) => i.codInstallatore);
      return cInstaller.map(({ codInstallatore, emailCert }) => ({ codInstallatore, emailCert }))
        .filter((i, index, self) => self.findIndex((s) => s.codInstallatore === i.codInstallatore) === index);
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly myEmail$: Observable<string> = this.authenticationService.user$.pipe(
    map((user) => user?.email ?? ''),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly is_servicePartner$ = this.roleService.is_servicePartner$;
  readonly is_baseCertificationInstaller$ = this.roleService.is_baseCertificationInstaller$;
  readonly is_advancedCertificationInstaller$ = this.roleService.is_advancedCertificationInstaller$;

  constructor(
    private readonly authenticationService: AuthenticationService,
    private readonly dialog: MatDialog,
    private readonly popoverService: PopoverService,
    private readonly roleService: RoleService,
    private readonly toastFacade: ToastFacade,
    private readonly uiPromiseService: UiPromiseService,
  ) { }

  async deleteRegistration(id: string, email: string) {
    if (this.mode === 'mobile') {
      await this.deleteRegistrationMobile(id, email);
    } else {
      await this.deleteRegistrationBrowser(id, email);
    }
  }

  async applyDissociateCertification(id: string, email: string) {
    await this.roleService.dissociateCertification(email, Number(id)).then(() => {
      this.toastFacade.present(this.labels.createdSuccess);
      this.roleService.refresh$.next();
      this.certifiedOperatorChangeEvent.emit();
    }).catch(() => this.toastFacade.present(this.labels.createdFailed, undefined,{ severity: 'error' }));
  }

  async deleteRegistrationMobile(id: string, email: string) {
    const deleteRegistrationPopover = await this.popoverService.simple({
      component: DialogComponent,
      componentProps: {
        name: this.labels.title,
        description: this.labels.description,
        primary: {
          click: async () => {
            await deleteRegistrationPopover.dismiss();
            await this.uiPromiseService.run(() => this.applyDissociateCertification(id, email), {
              loading: true,
              loadingMessage: this.labels.loading,
            });
          },
          name: this.labels.confirm,
        },
        secondary: {
          name: this.labels.cancel,
          click: () => deleteRegistrationPopover.dismiss(),
        },
      },
    });
  }

  async deleteRegistrationBrowser(id: string, email: string) {
    const dialogRef = this.dialog.open(DialogDataComponent, {
      width: '600px',
    });

    const actions = [
      {
        text: this.labels.cancel,
        action: () => dialogRef.close(),
      },
      {
        text: this.labels.confirm,
        cssClass: 'custom-mat-button-primary',
        focus: true,
        action: async () => {
          await this.uiPromiseService.run(() => this.applyDissociateCertification(id, email), {
            loading: true,
            loadingMessage: this.labels.loading,
          });
          dialogRef.close();
        },
      },
    ];

    dialogRef.componentInstance.data = {
      title: this.labels.title,
      content: this.labels.description,
      buttons: actions,
    };
  }
}
